import { cn } from '@/utils/cn';
import { LEAVE_DAY_REASON_LABELS, type LeaveDayLine } from '../types';

const WEEKDAYS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

interface LeaveDayTraceProps {
  days: LeaveDayLine[];
  total: number;
  countingMode: string;
  notes?: string[];
}

/**
 * La TRACE du décompte, jour par jour.
 *
 * C'est ce qui distingue un solde qu'on croit d'un solde qu'on peut défendre.
 * Un salarié qui voit « 6 jours » ouvre cette trace et lit les six lignes ;
 * un gestionnaire de paie qui conteste un total lit la même chose.
 */
export function LeaveDayTrace({ days, total, countingMode, notes }: LeaveDayTraceProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2">
        <span className="text-lg font-semibold text-neutral-900">
          {total.toLocaleString('fr-FR')}
        </span>
        <span className="text-sm text-neutral-500">
          jour{total > 1 ? 's' : ''} {countingMode === 'ouvrables' ? 'ouvrables' : 'ouvrés'},
          décomptés par le serveur
        </span>
      </div>

      <ul className="divide-y divide-neutral-100">
        {days.map(day => (
          <li
            key={day.date}
            className={cn(
              'flex items-center justify-between py-2 text-sm',
              day.counted === 0 && 'text-neutral-400',
            )}
          >
            <span className="min-w-0 truncate">
              <span className="font-medium text-neutral-700">
                {new Date(`${day.date}T00:00:00Z`).toLocaleDateString('fr-FR', {
                  timeZone: 'UTC',
                  day: '2-digit',
                  month: 'short',
                })}
              </span>
              <span className="ml-2 text-neutral-500">{WEEKDAYS[day.weekday]}</span>
            </span>
            <span className="flex items-center gap-4">
              <span className="text-neutral-500">
                {day.holidayLabel ?? LEAVE_DAY_REASON_LABELS[day.reason]}
              </span>
              <span
                className={cn(
                  'w-10 text-right tabular-nums',
                  day.counted > 0 ? 'font-medium text-neutral-900' : 'text-neutral-400',
                )}
              >
                {day.counted.toLocaleString('fr-FR')}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {notes && notes.length > 0 && (
        <ul className="space-y-1 text-sm text-neutral-500">
          {notes.map(note => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
